import React from 'react';
import styled from 'styled-components';
import { useMatches } from 'react-router-dom';
import { uniqueId } from 'lodash-es';

import { useAppSelector, useAppDispatch } from '../../../../../store';

import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';

import { testValues, sportNameToId } from '../../utils/functions';
import { SportType } from '../../utils/types';
import { prematchFetchSportByDay } from '../../../../../modules/bets/store/actions/prematch';
import { navSportsUpdate } from '../../../../../modules/bets/store/actions/navSports';

import './index.scss';

type NavigatorProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    dsType: string;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Navigator = (componentProps: NavigatorProps) => {
  let props = componentProps;
  let isVisible = true;

  const dataElementContext = React.useContext(DataElementContext);
  [props, isVisible] = processComponentProps(props, dataElementContext);

  const uriMatches = useMatches();
  const dispatch = useAppDispatch();
  const [__id] = React.useState(uniqueId('unique-'));
  const sportsMeta = useAppSelector<any>((state) => state.bets.prematch.sportsMeta);
  const filterByDate = useAppSelector<any>((state: any) => state.bets.calendar.filterBy);
  const filterByRange = useAppSelector<any>((state: any) => state.bets.calendar.range);
  const [extraTotal, setExtraTotal] = React.useState(0);
  const tid = React.useRef<any>(0);

  const idSport = React.useMemo(() => {
    let idSport = '';
    if (uriMatches?.length > 0) {
      uriMatches.find((match: any) => {
        if (match.params?.idSport) {
          idSport = match.params.idSport;
          return true;
        }
        return false;
      });
    }

    if (idSport) idSport = sportNameToId(idSport);
    if (!idSport) idSport = '1';
    return idSport;
  }, [uriMatches]);

  const [selectedIndexSport, selectedSport, sports] = React.useMemo(() => {
    let sports: SportType[] = Object.values(sportsMeta);

    // only active sports
    sports = sports.filter((sport: SportType) => {
      return sport?.sportActive === true;
    });

    // sort sport by backend sport position
    sports.sort((a, b) => {
      return testValues(a?.sportPosition, b?.sportPosition);
    });

    let selectedIndexSport = -1;
    let selectedSport: any = null;
    if (idSport) {
      selectedSport = sports.find((sport: SportType, index: number) => {
        if (sport?.idSport === idSport) {
          selectedIndexSport = index;
        }
        return sport?.idSport === idSport;
      });
    }
    return [selectedIndexSport, selectedSport, sports];
  }, [idSport, sportsMeta]);

  const onClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (e.currentTarget.dataset.sport) {
        const sport = e.currentTarget.dataset.sport;
        dispatch(navSportsUpdate('prematch', sport));

        let noDays = 1;
        if (filterByRange === '72h') {
          noDays = 3;
        }

        dispatch(prematchFetchSportByDay({ idSport: sport, day: filterByDate, noDays: noDays }));
      }
    },
    [dispatch, filterByDate, filterByRange],
  );

  const handleResize = React.useCallback(
    (timeout: any) => {
      window.clearTimeout(tid.current);
      tid.current = window.setTimeout(
        () => {
          let baseTop = 0;
          let items: any = document.querySelectorAll('.wl-prematch-navigator.mobile>.list-wrapper>.list>.item-wrapper');
          if (items.length) baseTop = items[0].offsetTop;

          const total = sports.length;
          let extraTotal = 0;

          if (items) {
            items = Array.from(items);
            items.find((item: any, index: number) => {
              if (item.offsetTop > baseTop) {
                extraTotal = total - index;
                return true;
              }

              return false;
            });

            setExtraTotal(extraTotal);
          }
        },
        typeof timeout === 'number' ? timeout : 100,
      );
    },
    [sports.length],
  );

  React.useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  React.useEffect(() => {
    handleResize(10);
  }, [sportsMeta, filterByDate, handleResize]);

  const contextValue = React.useMemo(
    () => ({
      sports,
      selectedSport,
      hiddenSport:
        selectedIndexSport > -1 && sports.length && extraTotal > 0 && selectedIndexSport >= sports.length - extraTotal
          ? true
          : false,
      extraTotal,
      onClick: onClick,
      __id: dataElementContext?.__id ?? __id,
      parentContext: dataElementContext,
    }),
    [sports, selectedSport, selectedIndexSport, extraTotal, onClick, dataElementContext, __id],
  );

  //console.log('NavigatorPrematch[contextValue]', contextValue);

  if (!isVisible) {
    return null;
  }

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Navigator;
